import React, {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
import NinjaTableFooter from './NinjaTableFooter';
import NinjaTableGrid from './NinjaTableGrid';
import NinjaTableWrapper from './NinjaTableWrapper';
import useColumns from './useColumns';
import ninjaApi from 'store/redux/apis/ninja.api';
import _isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import { useWidget } from 'components/Widget/useWidget';
import FileDownload from 'js-file-download';
import axios from 'axios';
import useQueryParams from 'hooks/useQueryParams';
import useLocalStorage from '@ninja/hooks/useLocalStorage';
import TableToolbar from 'components/tables/NinjaTable/TableToolbar';

export const NinjaTableContext = createContext({});

export const NinjaTable = (props) => {
  const {
    dataProvider,
    queryParams = {},
    /**
     * Parameter
     * @type {Object} savedFiltersParams
     */
    savedFiltersParams,
    enableSavedFilters,
    /**
     * Props that is send to table card wrapper
     * @type {Object} cardProps
     */
    cardProps = {},
    header,
    title,
    icon,
    showSelectRow = false,
    /**
     * If true, columns can be sorted horizontally
     */
    sortableColumns = true,
    sortable,
    /**
     * @todo move to table id or one unique identifier
     */
    customizationId,
    exportData,
    showSearch,
    /**
     * @todo should be passed from columns, or column should be disabled
     */
    removeColumns,
    rowHeight,
    /* table props */
    columns: allColumns = [],
    tableProps = {
      template: 'outerreporting',
      dynamicHeight: true,
    },
    footerProps = {
      showSizeChanger: true,
      readOnlyPagination: false,
      changeGlobally: false,
    },
    disableFooter = false,
    maxHeight = null,

    // datepicker props
    showExpandDays = false,
    date_key,

    /* table filter props */
    filterComponent,
    selectorComponent: SelectorComponent = Fragment,
    selectorProps = {},
    enableFilters,
    id,

    /* Other layout components */
    prependComponent,
    wrapperPrependComponent,

    rowKeyGetter = null,

    containerClassName,
    widget_key,
    defaultFilters = null,
    filterKeys = [],
    changeLevel,
    defaultSortColumns,
  } = props;

  const { searchParams } = useQueryParams();
  const { page = 1, per_page = 10 } = searchParams;
  /* Table State */

  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});

  // /** @var {[Set, () => {}]} */
  const [selectedRows, setSelectedRows] = useState({});
  const [statuses, setStatuses] = useState();
  const [localFilterText, setLocalFilterText] = useState('');
  const [extraQueryParams, setExtraQueryParams] = useState(queryParams);

  // table columns
  const [localColumns, setLocalColumns] = useLocalStorage({ key: customizationId });
  const visibleColumns = allColumns.filter((col) => !localColumns[col.key]?.hidden);
  const [columns, setColumns] = useState(visibleColumns || allColumns);

  useEffect(() => {
    if (customizationId) {
      setColumns(visibleColumns);
    } else {
      setColumns(allColumns);
    }
  }, [localColumns, allColumns, customizationId]);

  const updateCustomColumns = (columns) => {
    const tempCustomColumns = {};
    columns.forEach((c) => {
      if (!tempCustomColumns[c.key] && c.export !== false) {
        tempCustomColumns[c.key] = {
          key: c.key,
          hidden: c.hidden || false,
          name: c.title || c.name,
        };
      }
    });
    return tempCustomColumns;
  };

  useEffect(() => {
    if (customizationId) {
      setLocalColumns(updateCustomColumns(columns));
    }
  }, [allColumns, customizationId]);

  const onCustomizationUpdate = (val) => {
    setLocalColumns(updateCustomColumns(val));
  };

  const { dates: dates_redux } = useWidgetDate(date_key || 'global');

  const [filters, _setFilters] = useState({
    ...defaultFilters,
    date_from: dates_redux?.date_from,
    date_to: dates_redux?.date_to,
  });

  const [sortColumns, onSortColumnsChange] = useState(
    defaultSortColumns ? [defaultSortColumns] : []
  );
  const [pagination, setPagination] = useState({
    page: page,
    per_page: per_page,
    total_pages: 1,
  });

  const clearFilter = () => {
    _setFilters({});
  };

  useEffect(() => {
    if (defaultFilters) {
      _setFilters(defaultFilters);
    }
    setPagination({
      ...pagination,
      page: 1,
    });
  }, [defaultFilters]);

  const columnProps = useColumns(columns, {
    sortable: sortableColumns,
    id,
  });

  const [refetch, result] = (
    ninjaApi.endpoints[dataProvider] ? ninjaApi.endpoints[dataProvider] : ninjaApi.endpoints.fake
  ).useLazyQuery();

  const _data = Array.isArray(result.data) ? result.data : result.data?.data || [];

  /**
   * Load widget data
   */
  const { widget_filters, runFilterTimestamp } = useWidget(widget_key);

  const getRowKey = (row) => {
    if (typeof rowKeyGetter === 'function') {
      return rowKeyGetter(row);
    } else {
      return row.id;
    }
  };

  const selectRow = (row) => {
    setSelectedRows((prevState) => {
      prevState[getRowKey(row)] = row;
      return { ...prevState };
    });
  };

  const deselectRow = (row) => {
    setSelectedRows((prevState) => {
      delete prevState[getRowKey(row)];
      return { ...prevState };
    });
  };

  const selectAllRows = () => {
    const newRows = {};
    resultDataFiltered.forEach((row) => {
      newRows[getRowKey(row)] = row;
      return newRows;
    });
    setSelectedRows(newRows);
  };

  const isRowSelected = (row) => {
    return typeof selectedRows[getRowKey(row)] !== 'undefined';
  };

  const deselectAllRows = () => {
    setSelectedRows({});
  };

  const getSelectedRows = () => {
    return selectedRows;
  };

  /**
   * Fetch Data
   * @returns {void}
   */
  const fetchData = () => {
    const data = {
      filters: { ...filters, date_from: dates_redux?.date_from, date_to: dates_redux?.date_to },
      per_page: per_page,
      sort: sortColumns,
      ...extraQueryParams,
      ...pagination,
      ...queryParams,
      ...widget_filters,
    };
    refetch(data)
      ?.unwrap()
      .then((res) => {
        if (res.export_file_url && extraQueryParams.export) {
          axios({
            url: res.export_file_url,
            method: 'GET',
            responseType: 'blob',
          }).then((response) => {
            FileDownload(response.data, 'reporting.xlsx');
            setExtraQueryParams({
              export: false,
              export_option: '',
            });
          });
        }
      });
  };

  useEffect(() => {
    fetchData();
  }, [
    filters,
    statuses,
    pagination.page,
    pagination.per_page,
    sortColumns,
    extraQueryParams,
    runFilterTimestamp,
  ]);

  useLayoutEffect(() => {
    const pagination = result.data?.pagination;
    if (!pagination) {
      setPagination({
        page: page,
        per_page: per_page,
        total_pages: 1,
      });
    } else {
      setPagination({
        ...pagination,
      });
    }
  }, [result.data?.pagination]);

  useEffect(() => {
    const newQueryParams = { ...extraQueryParams, ...queryParams };
    if (_isEqual(newQueryParams, extraQueryParams)) {
      return;
    }

    setExtraQueryParams(newQueryParams);
  }, [queryParams]);

  const resultData = _data || [];
  const resultDataFiltered = localFilterText
    ? resultData.filter((d) =>
        Object.values(d).some((v) => (v?.includes ? v.includes(localFilterText) : false))
      )
    : resultData;

  const onExport = (value) => {
    setExtraQueryParams({
      export: true,
      export_range: value,
      columns: localColumns,
    });
  };

  const setFilters = (val) => {
    _setFilters((state) => {
      return { ...state, ...val };
    });
  };

  const isAllRowsSelected = React.useMemo(() => {
    return Object.keys(selectedRows).length >= resultDataFiltered.length;
  }, [selectedRows, resultDataFiltered]);

  const selectedRowsCount = React.useMemo(() => {
    return Object.keys(selectedRows).length;
  }, [selectedRows]);

  const isLoading = result.isFetching;

  const filterCount = (filters) => {
    let count = 0;

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value !== null && value !== undefined && value.length !== 0) {
        if (key === 'date_from' || key === 'date_to') {
          // Do not count date_from and date_to
        } else if (key === '_filterId') {
        } else if (key === 'registered_at_from' || key === 'registered_at_to') {
          count = 1;
        } else if (key === 'created_at_from' || key === 'created_at_to') {
          count = 1;
        } else if (typeof value === 'object') {
          const nestedCount = Object.entries(value).filter(
            ([nestedKey, nestedValue]) =>
              nestedKey !== 'type' &&
              nestedValue !== null &&
              nestedValue !== undefined &&
              nestedValue !== ''
          ).length;
          if (nestedCount > 0) {
            count++;
          }
        } else if (value !== '') {
          count++;
        }
      }
    });

    return count;
  };

  return (
    <NinjaTableContext.Provider
      value={{
        enableFilters,
        filters,
        setFilters,
        filterCount,
        pagination,
        setPagination,
        setStatuses,
        selectRow,
        deselectRow,
        selectAllRows,
        isRowSelected,
        deselectAllRows,
        getRowKey,
        getSelectedRows,
        isAllRowsSelected,
        selectedRowsCount,
        setSelectedRows,
        /** @var {Set} */
        selectedRows,
        extraQueryParams,
        setExtraQueryParams,
        allColumns,
        setColumns,
        setLocalFilterText,
        isLoading: isLoading,
        totalRows: result?.data?.totals,
        savedFilters,
        setSavedFilters,
        activeFilters,
        setActiveFilters,
        savedFiltersParams,
        enableSavedFilters,
        onExport,
        customizationId,
        onCustomizationUpdate,
        removeColumns,
        updateCustomColumns,
        exportData,
        showSearch,
        changeLevel,
        clearFilter,
        filterKeys,
      }}
    >
      {prependComponent ? prependComponent : null}
      <div className={clsx('flex flex-col w-full', containerClassName)}>
        <div className='col-span-12'>
          <TableToolbar
            filterComponent={filterComponent}
            wrapperPrependComponent={wrapperPrependComponent}
          />
        </div>
        <div className='sm:col-span-12'>
          <NinjaTableWrapper
            title={title}
            icon={icon}
            header={header}
            resultData={resultData}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            id={id}
            {...cardProps}
          >
            {showSelectRow && (
              <SelectorComponent
                pagination={pagination}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                setStatuses={setStatuses}
                resultData={resultData}
                filters={filters}
                {...selectorProps}
              />
            )}
            <NinjaTableGrid
              loading={isLoading}
              summaryRows={result?.data?.totals || []}
              resultData={resultData}
              rows={resultDataFiltered}
              sortColumns={sortColumns}
              onSortColumnsChange={onSortColumnsChange}
              wrapper={columnProps.DraggableComponent}
              {...columnProps}
              onSelectedRowsChange={setSelectedRows}
              maxHeight={maxHeight}
              rowHeight={rowHeight}
              defaultColumnOptions={{
                sortable: sortable,
              }}
              disableFooter={disableFooter}
              {...tableProps}
            />
            {!disableFooter && <NinjaTableFooter {...footerProps} />}
          </NinjaTableWrapper>
        </div>
      </div>
    </NinjaTableContext.Provider>
  );
};

export default NinjaTable;
